/* Brutalist Services — CATALOG.TBL. Monospace table with strict
   columns: ID / NAME / DURATION / PRICE / RUN. Terminal aesthetic. */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { tenantHref } from "@/lib/tenant";
import { EditorialSkeleton } from "../editorial/_EditorialSkeleton";

function priceFrom(svc) {
  const tiers = Array.isArray(svc?.tiers) ? svc.tiers : [];
  const tp = tiers.map(t => Number(t?.price)).filter(n => Number.isFinite(n) && n > 0);
  if (tp.length) return Math.min(...tp);
  const n = Number(svc?.price_from ?? svc?.starting_price ?? svc?.price);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export default function BrutalistServices() {
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const [services, setServices] = useState(null);
  useEffect(() => {
    api.get("/services").then(r => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => setServices([]));
    document.title = `${studioName} // CATALOG`;
  }, [studioName]);

  if (services === null) return <EditorialSkeleton eyebrow="// loading catalog" />;

  return (
    <div data-testid="brutalist-services"
      style={{ background: "var(--tenant-bg, #0C0A09)", color: "var(--tenant-fg, #FAFAF8)", fontFamily: "var(--tenant-sans, monospace)" }}>

      <section className="px-6 lg:px-12 pt-16 pb-10">
        <div className="max-w-[1480px] mx-auto">
          <div className="text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--tenant-accent)" }}>
            // CATALOG.TBL
          </div>
          <div className="flex items-center gap-6 mt-4 mb-12 pb-4 border-b" style={{ borderColor: "var(--tenant-accent)" }}>
            <h1 className="leading-[0.85] uppercase"
                style={{ fontFamily: "var(--tenant-serif, monospace)", fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
              CATALOG
            </h1>
            <div className="flex-1 h-px" style={{ background: "var(--tenant-accent)" }} />
            <div className="text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--tenant-fg-soft)" }}>
              [ROWS] : {String(services.length).padStart(3, "0")}
            </div>
          </div>
        </div>
      </section>

      <div className="px-6 lg:px-12 pb-32 max-w-[1480px] mx-auto">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] uppercase tracking-[0.12em]">
            <thead>
              <tr className="text-left text-[10px]" style={{ color: "var(--tenant-fg-soft)", borderBottom: "1px solid var(--tenant-accent)" }}>
                <th className="py-4 pr-4">ID</th>
                <th className="py-4 pr-4">NAME</th>
                <th className="py-4 pr-4 hidden md:table-cell">DESC</th>
                <th className="py-4 pr-4">DUR</th>
                <th className="py-4 pr-4 text-right">PRICE</th>
                <th className="py-4 text-right">─</th>
              </tr>
            </thead>
            <tbody>
              {services.map((svc, i) => {
                const p = priceFrom(svc);
                return (
                  <tr key={svc.id} id={svc.id || svc.slug} className="border-b align-top"
                      style={{ borderColor: "color-mix(in srgb, var(--tenant-accent) 25%, transparent)" }}>
                    <td className="py-6 pr-4 tabular-nums" style={{ color: "var(--tenant-accent)" }}>{String(i + 1).padStart(2, "0")}</td>
                    <td className="py-6 pr-4 font-bold">{svc.name}</td>
                    <td className="py-6 pr-4 hidden md:table-cell" style={{ color: "var(--tenant-fg-soft)" }}>
                      {(svc.description || svc.short_description)?.slice(0, 60) || "─"}
                    </td>
                    <td className="py-6 pr-4 tabular-nums" style={{ color: "var(--tenant-fg-soft)" }}>
                      {svc.duration_minutes ? `${svc.duration_minutes}M` : "VAR"}
                    </td>
                    <td className="py-6 pr-4 text-right tabular-nums" style={{ color: "var(--tenant-accent)" }}>
                      {p !== null ? `$${p.toFixed(0)}` : "─"}
                    </td>
                    <td className="py-6 text-right">
                      <Link to={tenantHref("/booking")} className="underline-offset-4 hover:underline">RUN [→]</Link>
                    </td>
                  </tr>
                );
              })}
              {services.length === 0 && (
                <tr><td colSpan="6" className="py-12 text-center text-[10px]" style={{ color: "var(--tenant-fg-soft)" }}>// empty.tbl</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
